import { useRef, useState, type ReactElement } from 'react';
import {
  ApiError,
  CONNECTED_APP_SELF_SERVICE_ISSUANCE,
  type ConnectedAppConsentBody,
  type ConnectedAppGrantStatus,
  type ConnectedAppScope,
  type ConnectedAppTokenReveal,
  type MemberConnectedAppGrant,
} from '@molt/shared';
import {
  Button,
  CardSurface,
  Dialog,
  Menu,
  Notice,
  TextField,
  type MenuItem,
} from '@/components';
import { RelativeTime } from '@/components/RelativeTime';
import { apiTokenSnippets } from '@/lib/api-token-snippets';
import { parseCardTimestamp } from '@/lib/card-dates';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { formatLocaleDateTimeLabel } from '@/lib/locale-formatting';
import { useLocalePreferencesValue } from '@/lib/locale-preferences';

export type Translate = (key: string, values?: Record<string, string | number>) => string;

/**
 * FE-80.1 — `SCOPE_LABELS` was a module-level `Record<ConnectedAppScope,
 * string>` and a module-level constant cannot call `t`. This is the shape
 * `languageLabel(t, language)` and `timezoneLabel(t, timezone)` already use one
 * file away in `lib/locale-formatting.ts`: the mapping from union member to key
 * lives in exactly ONE place, so the two call sites below — the trigger's own
 * label and each grant row's scope line — cannot drift apart.
 *
 * Exported, with `GrantDate` and `ExpiryDate`, for B85's admin tokens section
 * (`components/admin/ApiTokensAdminSection.tsx`), so a superadmin and the
 * member read one spelling of a scope, a relative time and an absolute expiry.
 */
export function scopeLabel(t: Translate, scope: ConnectedAppScope): string {
  return scope === 'read_write'
    ? t('profile.connectedTools.scope.readWrite')
    : t('profile.connectedTools.scope.readOnly');
}

/**
 * AT-13 — the scope line carries the grant's STATUS as one whole slotted value
 * per state, the same shape `revokedScope` already had, so a locale can reorder
 * the two halves or change the separator. `unclaimed` is worded for the member
 * ("Not yet revealed"): it is an issued grant whose token nobody holds yet.
 */
function scopeLine(
  t: Translate,
  scope: ConnectedAppScope,
  status: ConnectedAppGrantStatus,
): string {
  const values = { scope: scopeLabel(t, scope) };
  switch (status) {
    case 'unclaimed':
      return t('profile.connectedTools.unclaimedScope', values);
    case 'active':
      return t('profile.connectedTools.activeScope', values);
    case 'expired':
      return t('profile.connectedTools.expiredScope', values);
    case 'revoked':
      return t('profile.connectedTools.revokedScope', values);
  }
}

/** Which of the two token actions a dialog is confirming. */
export type ConnectedToolTokenAction = 'reveal' | 'regenerate';

export interface ConnectedToolsSectionProps {
  readonly grants: readonly MemberConnectedAppGrant[] | undefined;
  readonly loading?: boolean | undefined;
  readonly loadError?: string | undefined;
  readonly createdToken?: string | null | undefined;
  readonly createBusy?: boolean | undefined;
  readonly revokeBusyId?: string | null | undefined;
  readonly createError?: string | undefined;
  readonly revokeErrors?: Readonly<Record<string, string>> | undefined;
  readonly onCreate: (body: ConnectedAppConsentBody) => void;
  readonly onRevoke: (id: string) => void;
  /**
   * AT-8 — reveal or regenerate one grant's token. The token comes back in the
   * resolved value and is held in THIS component's state, for as long as the
   * dialog is open, and nowhere else.
   */
  readonly onRevealToken: (
    id: string,
    action: ConnectedToolTokenAction,
  ) => Promise<ConnectedAppTokenReveal>;
  /**
   * AT-6's switch, read from `CONNECTED_APP_SELF_SERVICE_ISSUANCE` by default,
   * which is `false`: the create form is hidden and `onCreate` is unreachable.
   * A prop rather than a module read so the reopened branch stays testable
   * while the constant is closed.
   */
  readonly selfServiceIssuance?: boolean | undefined;
  /**
   * The API base the snippets name. Defaults to this origin's `/api`, which is
   * where both the dev proxy and production serve it.
   */
  readonly apiBaseUrl?: string | undefined;
}

/**
 * The menu's two radio items, built per render for the same reason
 * `scopeLabel` is a function: a module constant freezes English. The `id`s are
 * the wire values and stay literal.
 */
function scopeItems(t: Translate): MenuItem[] {
  return [
    { id: 'read_only', label: scopeLabel(t, 'read_only'), onSelect: () => undefined },
    { id: 'read_write', label: scopeLabel(t, 'read_write'), onSelect: () => undefined },
  ];
}

/**
 * Reaches `t` itself rather than taking a `never` label prop: it renders in
 * three columns of every grant row, and threading one label through three call
 * sites is three chances to pass the wrong one.
 */
export function GrantDate({ value }: { value: string | null }): ReactElement {
  const { t } = useInterfaceLanguage();
  if (value === null) return <span>{t('profile.connectedTools.never')}</span>;
  return <RelativeTime value={value} />;
}

/**
 * AT-4 edge case — an expiry is an ABSOLUTE date in the viewer's language and
 * time zone (B44), never only "in 90 days". Rendered inside `<time>` so the
 * machine-readable instant is on the node, and so the interface-language sweep
 * excludes it by element: a medium date contains a month name.
 */
export function ExpiryDate({ value }: { value: string | null }): ReactElement {
  const { t } = useInterfaceLanguage();
  const preferences = useLocalePreferencesValue();
  if (value === null) return <span>{t('profile.connectedTools.never')}</span>;
  const parsed = parseCardTimestamp(value);
  return (
    <time dateTime={parsed?.toISOString() ?? value}>
      {formatLocaleDateTimeLabel(t, value, preferences)}
    </time>
  );
}

/** The dialog's subject, snapshotted when it opens so a refetch cannot move it. */
interface TokenDialogSubject {
  readonly id: string;
  readonly toolName: string;
  readonly scope: ConnectedAppScope;
  readonly expiresAt: string | null;
  readonly action: ConnectedToolTokenAction;
}

function Snippet({
  heading,
  copyLabel,
  text,
  copyDisabledReason,
  onCopy,
}: {
  heading: string;
  copyLabel: string;
  text: string;
  copyDisabledReason: string | null;
  onCopy: (text: string) => void;
}): ReactElement {
  return (
    <div className="flex flex-col gap-1">
      <h3 className="text-sm font-semibold text-ink">{heading}</h3>
      {/* A command, not copy: excluded from the interface-language sweep by
          the `pre` element, and read back verbatim by its own test. */}
      <pre className="overflow-x-auto rounded-card border border-border bg-surface-raised p-2 font-mono text-xs text-ink">
        <code>{text}</code>
      </pre>
      <div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={copyDisabledReason !== null}
          disabledReason={copyDisabledReason ?? undefined}
          onClick={() => onCopy(text)}
        >
          {copyLabel}
        </Button>
      </div>
    </div>
  );
}

export function ConnectedToolsSection({
  grants,
  loading = false,
  loadError,
  createdToken = null,
  createBusy,
  revokeBusyId,
  createError,
  revokeErrors,
  onCreate,
  onRevoke,
  onRevealToken,
  selfServiceIssuance = CONNECTED_APP_SELF_SERVICE_ISSUANCE,
  apiBaseUrl,
}: ConnectedToolsSectionProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const [dialog, setDialog] = useState<TokenDialogSubject | null>(null);
  /*
   * ⚠ The cleartext token lives HERE and only here: never the query cache,
   * never storage, never the URL. Every way out of the dialog goes through
   * `closeDialog`, which drops it.
   */
  const [revealedToken, setRevealedToken] = useState<string | null>(null);
  const [revealBusy, setRevealBusy] = useState(false);
  const [revealError, setRevealError] = useState<string | null>(null);
  const [copyNotice, setCopyNotice] = useState<{
    tone: 'info' | 'bad';
    text: string;
  } | null>(null);
  /*
   * Bumped whenever the dialog opens or closes. The dialog cannot be dismissed
   * while a reveal is in flight (see `dismissDialog`), so this is defence in
   * depth: a result that still lands after its dialog is gone is discarded
   * instead of surfacing a token nobody is looking at.
   */
  const requestGeneration = useRef(0);
  const [toolName, setToolName] = useState('');
  const [scope, setScope] = useState<ConnectedAppScope>('read_only');
  const trimmedToolName = toolName.trim();
  const createDisabledReason =
    trimmedToolName === '' ? t('profile.connectedTools.nameRequired') : null;
  const createBusyProps = createBusy === undefined ? {} : { busy: createBusy };
  const createDisabledProps =
    createDisabledReason === null
      ? {}
      : { disabled: true, disabledReason: createDisabledReason };
  const activeGrants = grants?.filter((grant) => grant.revokedAt === null) ?? [];
  const revokedGrants = grants?.filter((grant) => grant.revokedAt !== null) ?? [];

  function submitGrant(): void {
    if (trimmedToolName === '') return;
    onCreate({ toolName: trimmedToolName, scope, expiresAt: null });
  }

  const clipboardAvailable =
    typeof navigator !== 'undefined' && navigator.clipboard?.writeText !== undefined;
  const copyDisabledReason = clipboardAvailable
    ? null
    : t('profile.connectedTools.copyUnavailable');
  const snippets =
    revealedToken === null
      ? null
      : apiTokenSnippets(apiBaseUrl ?? `${window.location.origin}/api`, revealedToken);

  function openDialog(grant: MemberConnectedAppGrant, action: ConnectedToolTokenAction) {
    requestGeneration.current += 1;
    setRevealedToken(null);
    setRevealError(null);
    setRevealBusy(false);
    setCopyNotice(null);
    setDialog({
      id: grant.id,
      toolName: grant.toolName,
      scope: grant.scope,
      expiresAt: grant.expiresAt,
      action,
    });
  }

  function closeDialog(): void {
    requestGeneration.current += 1;
    setDialog(null);
    setRevealedToken(null);
    setRevealError(null);
    setRevealBusy(false);
    setCopyNotice(null);
  }

  /*
   * ⚠ Every dismissal — the quiet footer button, Escape, a click outside — is
   * refused while a reveal is in flight. By then the server may already have
   * revoked the current token and committed its successor; closing would throw
   * away the only copy of the new token and leave the member with none.
   */
  function dismissDialog(): void {
    if (revealBusy) return;
    closeDialog();
  }

  async function confirmReveal(): Promise<void> {
    if (dialog === null || revealBusy) return;
    const generation = requestGeneration.current;
    setRevealBusy(true);
    setRevealError(null);
    try {
      const revealed = await onRevealToken(dialog.id, dialog.action);
      if (generation !== requestGeneration.current) return;
      setRevealedToken(revealed.token);
    } catch (error) {
      if (generation !== requestGeneration.current) return;
      setRevealError(
        error instanceof ApiError
          ? error.details?.['connectedApp']?.includes('grant_not_live') === true
            ? t('profile.connectedTools.notLive')
            : error.message
          : t('profile.connectedTools.revealFailed'),
      );
    } finally {
      if (generation === requestGeneration.current) setRevealBusy(false);
    }
  }

  async function copy(text: string): Promise<void> {
    if (!clipboardAvailable) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopyNotice({ tone: 'info', text: t('profile.connectedTools.copied') });
    } catch {
      setCopyNotice({ tone: 'bad', text: t('profile.connectedTools.copyFailed') });
    }
  }

  return (
    <section aria-labelledby="connected-tools-heading" className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 id="connected-tools-heading" className="text-base font-semibold text-ink">
          {t('profile.connectedTools.heading')}
        </h2>
        <p className="text-sm text-ink-muted">
          {t('profile.connectedTools.description')}
        </p>
      </div>

      {selfServiceIssuance ? (
        <CardSurface variant="sheet" className="flex flex-col gap-3 p-4">
          <h3 className="text-sm font-semibold text-ink">
            {t('profile.connectedTools.grantHeading')}
          </h3>
          <TextField
            label={t('profile.connectedTools.toolName')}
            value={toolName}
            onChange={(event) => setToolName(event.target.value)}
            maxLength={255}
          />
          <Menu
            trigger={
              <Button
                type="button"
                variant="secondary"
                /* Was a template join of the scope label and an English tail.
                 A join is not translatable — word order and the dash are both
                 language-specific — so the whole sentence is one slotted
                 value, the way `CardPrintView`'s hard-coded `', '` still is
                 not. */
                aria-label={t('profile.connectedTools.scopeTrigger', {
                  scope: scopeLabel(t, scope),
                })}
              >
                {scopeLabel(t, scope)}
              </Button>
            }
            sections={[
              {
                id: 'connected-tool-scope',
                heading: t('profile.connectedTools.scopeHeading'),
                selection: 'radio',
                value: scope,
                onValueChange: (value) => setScope(value as ConnectedAppScope),
                items: scopeItems(t),
              },
            ]}
          />
          {/* Stays while self-service is open: it describes the grant THIS form
            creates, which is sent with `expiresAt: null`. Issued grants show
            their real expiry on their own row instead (AT-13). */}
          <p className="text-sm text-ink-muted">
            {t('profile.connectedTools.grantExpiry')}
          </p>
          <div>
            <Button
              type="button"
              variant="primary"
              onClick={submitGrant}
              {...createBusyProps}
              {...createDisabledProps}
            >
              {t('profile.connectedTools.create')}
            </Button>
          </div>
          {createdToken !== null ? (
            <TextField
              label={t('profile.connectedTools.tokenLabel')}
              readOnly
              value={createdToken}
              hint={t('profile.connectedTools.tokenHint')}
            />
          ) : null}
          {createError !== undefined ? <Notice tone="bad" body={createError} /> : null}
        </CardSurface>
      ) : (
        <p className="text-sm text-ink-muted">
          {t('profile.connectedTools.adminIssuedNote')}
        </p>
      )}

      {loadError !== undefined ? (
        <Notice tone="bad" body={loadError} />
      ) : loading || grants === undefined ? (
        <Notice tone="info" body={t('profile.connectedTools.loading')} />
      ) : activeGrants.length === 0 && revokedGrants.length === 0 ? (
        <CardSurface variant="sheet" className="p-4 text-sm text-ink-muted">
          {t('profile.connectedTools.empty')}
        </CardSurface>
      ) : (
        // The same key as the `h2` above, at its second call site: both name
        // the same region, and two keys would let a translation name it twice.
        <div
          role="list"
          aria-label={t('profile.connectedTools.heading')}
          className="flex flex-col gap-2"
        >
          {[...activeGrants, ...revokedGrants].map((grant) => {
            const revoked = grant.revokedAt !== null;
            // `revokedAt` wins over a stale `status`: a cache row written by an
            // older writer can lag, and a revoked grant must never offer Reveal.
            const status: ConnectedAppGrantStatus = revoked ? 'revoked' : grant.status;
            const busy = revokeBusyId === grant.id;
            const busyProps = revokeBusyId === undefined ? {} : { busy };
            return (
              <CardSurface
                key={grant.id}
                variant="sheet"
                role="listitem"
                className="flex flex-col gap-3 p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="break-words text-sm font-semibold text-ink">
                      {grant.toolName}
                    </h3>
                    {/* Was the scope label with a bare `' · Revoked'`
                        appended. The revoked arm is now a WHOLE second value
                        rather than a fragment, so a locale can put the state
                        first or change the separator; the live arm is the plain
                        label, not a slotted sentence with an empty tail. */}
                    <p className="text-sm text-ink-muted">
                      {scopeLine(t, grant.scope, status)}
                    </p>
                    {grant.issuedBy !== null ? (
                      <p className="text-sm text-ink-muted">
                        {t('profile.connectedTools.issuedBy', {
                          name: grant.issuedBy.displayName,
                        })}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap justify-end gap-2">
                    {status === 'unclaimed' ? (
                      <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        aria-label={t('profile.connectedTools.revealFor', {
                          toolName: grant.toolName,
                        })}
                        onClick={() => openDialog(grant, 'reveal')}
                      >
                        {t('profile.connectedTools.reveal')}
                      </Button>
                    ) : status === 'active' ? (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        aria-label={t('profile.connectedTools.regenerateFor', {
                          toolName: grant.toolName,
                        })}
                        onClick={() => openDialog(grant, 'regenerate')}
                      >
                        {t('profile.connectedTools.regenerate')}
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      disabled={revoked}
                      disabledReason={
                        revoked
                          ? t('profile.connectedTools.alreadyRevoked', {
                              toolName: grant.toolName,
                            })
                          : undefined
                      }
                      onClick={() => onRevoke(grant.id)}
                      {...busyProps}
                    >
                      {t('profile.connectedTools.revoke', { toolName: grant.toolName })}
                    </Button>
                  </div>
                </div>
                <dl
                  className={
                    grant.tokenHint === null
                      ? 'grid gap-2 text-sm sm:grid-cols-3'
                      : 'grid gap-2 text-sm sm:grid-cols-4'
                  }
                >
                  <div>
                    <dt className="text-xs text-ink-muted">
                      {t('profile.connectedTools.granted')}
                    </dt>
                    <dd className="text-ink">
                      <GrantDate value={grant.grantedAt} />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-ink-muted">
                      {t('profile.connectedTools.lastActive')}
                    </dt>
                    <dd className="text-ink">
                      <GrantDate value={grant.lastActiveAt} />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-ink-muted">
                      {t('profile.connectedTools.expires')}
                    </dt>
                    <dd className="text-ink">
                      <ExpiryDate value={grant.expiresAt} />
                    </dd>
                  </div>
                  {grant.tokenHint !== null ? (
                    <div>
                      <dt className="text-xs text-ink-muted">
                        {t('profile.connectedTools.tokenEndingLabel')}
                      </dt>
                      <dd className="font-mono text-ink">
                        {t('profile.connectedTools.tokenEnding', {
                          hint: grant.tokenHint,
                        })}
                      </dd>
                    </div>
                  ) : null}
                </dl>
                {revokeErrors?.[grant.id] !== undefined ? (
                  <Notice tone="bad" body={revokeErrors[grant.id]} />
                ) : null}
              </CardSurface>
            );
          })}
        </div>
      )}

      {dialog !== null ? (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open) dismissDialog();
          }}
          size="md"
          title={
            revealedToken !== null
              ? t('profile.connectedTools.revealed.title', { toolName: dialog.toolName })
              : dialog.action === 'reveal'
                ? t('profile.connectedTools.revealDialog.title', {
                    toolName: dialog.toolName,
                  })
                : t('profile.connectedTools.regenerateDialog.title', {
                    toolName: dialog.toolName,
                  })
          }
          description={
            revealedToken !== null
              ? undefined
              : dialog.action === 'reveal'
                ? t('profile.connectedTools.revealDialog.description')
                : t('profile.connectedTools.regenerateDialog.description')
          }
          footer={
            revealedToken !== null ? (
              <Button type="button" variant="primary" onClick={closeDialog}>
                {t('profile.connectedTools.revealed.close')}
              </Button>
            ) : (
              <>
                <Button type="button" variant="quiet" onClick={dismissDialog}>
                  {dialog.action === 'reveal'
                    ? t('profile.connectedTools.revealDialog.dismiss')
                    : t('profile.connectedTools.regenerateDialog.dismiss')}
                </Button>
                <Button
                  type="button"
                  variant={dialog.action === 'reveal' ? 'primary' : 'danger'}
                  busy={revealBusy}
                  onClick={() => {
                    void confirmReveal();
                  }}
                >
                  {dialog.action === 'reveal'
                    ? t('profile.connectedTools.revealDialog.confirm')
                    : t('profile.connectedTools.regenerateDialog.confirm')}
                </Button>
              </>
            )
          }
        >
          {revealedToken === null || snippets === null ? (
            <div className="flex flex-col gap-3">
              {/* FR-AUTO-033 AC1's consent screen: what the token is, what it
                  may do and when it stops, all BEFORE the confirm button. */}
              <dl className="grid gap-2 text-sm sm:grid-cols-3">
                <div>
                  <dt className="text-xs text-ink-muted">
                    {t('profile.connectedTools.toolName')}
                  </dt>
                  <dd className="break-words text-ink">{dialog.toolName}</dd>
                </div>
                <div>
                  <dt className="text-xs text-ink-muted">
                    {t('profile.connectedTools.scopeHeading')}
                  </dt>
                  <dd className="text-ink">{scopeLabel(t, dialog.scope)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-ink-muted">
                    {t('profile.connectedTools.expires')}
                  </dt>
                  <dd className="text-ink">
                    <ExpiryDate value={dialog.expiresAt} />
                  </dd>
                </div>
              </dl>
              {revealError !== null ? <Notice tone="bad" body={revealError} /> : null}
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <Notice tone="warn" body={t('profile.connectedTools.revealed.warning')} />
              <div className="flex flex-col gap-2">
                <TextField
                  label={t('profile.connectedTools.revealed.tokenLabel')}
                  readOnly
                  value={revealedToken}
                  className="font-mono"
                />
                <div>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={copyDisabledReason !== null}
                    disabledReason={copyDisabledReason ?? undefined}
                    onClick={() => {
                      void copy(revealedToken);
                    }}
                  >
                    {t('profile.connectedTools.revealed.copyToken')}
                  </Button>
                </div>
              </div>
              <Snippet
                heading={t('profile.connectedTools.revealed.exportHeading')}
                copyLabel={t('profile.connectedTools.revealed.copyExport')}
                text={snippets.exportLine}
                copyDisabledReason={copyDisabledReason}
                onCopy={(text) => {
                  void copy(text);
                }}
              />
              <Snippet
                heading={t('profile.connectedTools.revealed.mcpHeading')}
                copyLabel={t('profile.connectedTools.revealed.copyMcp')}
                text={snippets.mcp}
                copyDisabledReason={copyDisabledReason}
                onCopy={(text) => {
                  void copy(text);
                }}
              />
              <Snippet
                heading={t('profile.connectedTools.revealed.curlHeading')}
                copyLabel={t('profile.connectedTools.revealed.copyCurl')}
                text={snippets.curl}
                copyDisabledReason={copyDisabledReason}
                onCopy={(text) => {
                  void copy(text);
                }}
              />
              {copyNotice !== null ? (
                <Notice tone={copyNotice.tone} body={copyNotice.text} />
              ) : null}
            </div>
          )}
        </Dialog>
      ) : null}
    </section>
  );
}
